import axios from "axios";
import { apiCall } from "@/common/api/ApiResult.model";
import {
  EventLabelDir,
  Insight,
  InsightHelper,
  AppsInsightSeverityLevel,
  INSIGHT_SHARE_TYPE_BLACK_LIST,
} from "@/common/models/insight.model";
import { cord, gql2, tier12Params } from "@/gql/lib";
import { ActionEnum, AxisType, GroupByEnum } from "@/gql/types";
import { prop_from_prop, reducers, surface_prop, merge_by } from "@/fp";

export const getAllInsights = apiCall<void, Insight[]>(
  "all-insights",
  async () => {
    const res = await axios.get("/insight");
    return {
      success: true,
      value: res.data
        .filter(
          (d: any) =>
            !INSIGHT_SHARE_TYPE_BLACK_LIST[d.extraProperties.shareType]
        )
        .map(InsightHelper.fromApiInsight),
    };
  }
);

export const getInsightsDesc = apiCall<void, { [key: string]: string }>(
  "label-and-insight-desc",
  async () => {
    const res = await axios({
      method: "GET",
      baseURL: "",
      url: "/json/insights.mapping.json",
    });
    return {
      success: true,
      // @ts-ignore
      value: res.data.reduce((acc, item) => {
        acc[item.id] = item.description;
        return acc;
      }, {}),
    };
  }
);

export const get_insights_sender_receiver_counts = gql2<
  Array<{ id: string; eventLabel: EventLabelDir }>,
  any
>("insights-counters.get", (arr, timeFrame) => {
  const ids = arr.map((l) => l.id);
  return {
    query: `{
      events : tier2count(params:${tier12Params({
        coordinate: {
          x: cord(AxisType.None, []),
          y: {
            type: AxisType.Insight,
            values: ids,
          },
          z: timeFrame.frame,
        },
        action: ActionEnum.Sum,
      })}){
        count
        y{value}
      }

      EmployeeSender: tier2count(params:${tier12Params({
        coordinate: {
          x: cord(AxisType.EmployeeSender, []),
          y: {
            type: AxisType.Insight,
            values: ids,
          },
          z: timeFrame.frame,
        },
        action: ActionEnum.RecordCount,
        groupBy: GroupByEnum.YValue,
      })}){
        count
        y{value}
      }
    }`,

    mapper: (res) => {
      // @ts-ignore
      const merge_by_id = merge_by("id");

      const y_value_to_id = surface_prop("y.value", "id");

      const { EmployeeSender, events } = res;

      const [employee_sender_arr, events_arr] = [
        EmployeeSender,
        events,
      ].map((arr) => arr.map(y_value_to_id));

      const result = merge_by_id(
        ids.map((id) => ({
          id,
          eventsCount: 0,
          exposedToCount: 0,
          exposedByCount: 0,
        }))
      )(
        employee_sender_arr.map(prop_from_prop("count", "exposedByCount")),
        events_arr.map(prop_from_prop("count", "eventsCount"))
      );

      return result;
    },
  };
});

export const getInsightsSenderReceiverCounts = gql2<
  { id: string; eventLabel: EventLabelDir },
  { [key: string]: { sender: number; receiver: number; events: number } }
>("insights-counters.get", ({ id, eventLabel }, timeFrame) => {
  return {
    query: `{
          events : tier2count(params:${tier12Params({
            coordinate: {
              x: cord(AxisType.None, []),
              y: cord(AxisType.Insight, [id]),
              z: timeFrame.frame,
            },
            action: ActionEnum.List,
          })}){
            count
          }
          sender: tier2count(params:${tier12Params({
            coordinate: {
              x: cord(
                eventLabel === "inside"
                  ? AxisType.EmployeeSender
                  : AxisType.PersonSender,
                []
              ),
              y: cord(AxisType.Insight, [id]),
              z: timeFrame.frame,
            },
            action: ActionEnum.RecordCount,
            groupBy: GroupByEnum.YValue,
          })}){
            count
            y{value}
          }
          receiver: tier2count(params:${tier12Params({
            coordinate: {
              x: cord(
                eventLabel === "inside"
                  ? AxisType.EmployeeReceiver
                  : AxisType.PersonReceiver,
                []
              ),
              y: cord(AxisType.Insight, [id]),
              z: timeFrame.frame,
            },
            action: ActionEnum.RecordCount,
            groupBy: GroupByEnum.YValue,
          })}){
            count
            y{value}
          }
        }`,
    mapper: (res) => {
      const { sender, receiver, events } = res;
      const sum = reducers.sumProp("count");
      const result = {
        [id]: {
          sender: sender.reduce(sum, 0),
          receiver: receiver.reduce(sum, 0),
          events: events.reduce(sum, 0),
        },
      };
      return result;
    },
  };
});

export const updateInsight = apiCall<
  { id: string; severity: AppsInsightSeverityLevel },
  boolean
>("update-insight", async (payload: { id: string; severity: string }) => {
  await axios.patch("/insight", [payload]);
  return {
    success: true,
    value: true,
  };
});
